import {
  Container,
  Title,
  Text,
  Accordion,
  Box,
  Stack,
  useComputedColorScheme,
} from "@mantine/core";
import { IconQuestionMark } from "@tabler/icons-react";
import { AnimateIn } from "./AnimateIn";

const faqs = [
  {
    question: "How long does implementation take?",
    answer:
      "Most facilities are up and running within 2-4 weeks. We provide personalized onboarding, data migration assistance, and comprehensive training for your team. Our white-glove setup ensures a smooth transition with minimal disruption to your operations.",
  },
  {
    question: "Do you provide data migration from our current system?",
    answer:
      "Yes. We offer data migration assistance for new customers — we'll work with you to import resident records, employee data, and historical information from spreadsheets, existing software, or paper records.",
  },
  {
    question: "Is LivingSyncs built for HIPAA?",
    answer:
      "Yes. LivingSyncs is built for HIPAA from the ground up: comprehensive PHI protection, role-based access control, audit logging on every record, MFA on administrative access, and AES-256 encryption at rest with TLS 1.3 in transit. We sign a Business Associate Agreement with every customer. HIPAA compliance is a shared responsibility — we provide the safeguards and the BAA; your policies and workforce training complete the picture.",
  },
  {
    question: "What integrations are available?",
    answer:
      "LivingSyncs offers CSV export capabilities for all data, webhook support for real-time notifications, and API access (Premium tier). We're actively building integrations with popular EHR systems and pharmacy management systems. Custom integrations are available for Premium customers.",
  },
  {
    question: "Can I try it before committing?",
    answer:
      "Yes! We offer a 7-day free trial with full access to all Standard features. No credit card required. During your trial, you'll have access to our support team and can schedule a personalized demo to explore features specific to your needs.",
  },
  {
    question: "What kind of support do you provide?",
    answer:
      "Standard plan includes email support with a 24-hour response target and product documentation. Premium plan includes priority support and phone support. All customers get onboarding assistance when they start.",
  },
  {
    question: "Can I add or remove facilities at any time?",
    answer:
      "Yes! Our pricing scales with your organization. You can add new facilities instantly and only pay for the months they're active. If you need to remove a facility, we prorate the billing accordingly. No long-term contracts required.",
  },
  {
    question: "What happens to my data if I cancel?",
    answer:
      "You own your data, always. If you decide to cancel, we provide a complete data export in CSV format including all resident records, forms, notes, and historical data. Your data remains accessible for 30 days after cancellation for download.",
  },
  {
    question: "Do you offer training for our staff?",
    answer:
      "Yes! All plans include comprehensive onboarding training via video conference. We provide role-specific training sessions for administrators, medical staff, and care staff. You'll also have access to our extensive video tutorial library and documentation portal.",
  },
  {
    question: "Is mobile access included?",
    answer:
      "Yes! LivingSyncs is fully responsive and works seamlessly on tablets and smartphones. Staff can clock in/out, document care notes, administer medications, and view schedules from any mobile device. No separate mobile app needed.",
  },
];

export function FAQSection() {
  const computedColorScheme = useComputedColorScheme("light");

  return (
    <Box
      component="section"
      id="faq"
      py={100}
      bg={computedColorScheme === "dark" ? "#25262B" : "#f8f9fa"}
    >
      <Container size="md">
        <AnimateIn>
          <Stack align="center" gap="md" mb={60}>
            <Title order={2} size="2.5rem" fw={700} ta="center">
              Frequently Asked{" "}
              <Text
                component="span"
                inherit
                style={{
                  background:
                    "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Questions
              </Text>
            </Title>
            <Text size="lg" c="dimmed" ta="center" maw={600}>
              Everything you need to know about getting started with LivingSyncs
            </Text>
          </Stack>
        </AnimateIn>

        <AnimateIn delay={0.1}>
          <Accordion
            variant="separated"
            radius="md"
            chevronPosition="right"
            // Item background/border and the open + hover states all live in
            // index.css — see the note there. They have to stay together: a
            // background set through `styles` lands as an inline style and
            // would outrank the [data-active] rule.
            className="faq-accordion"
            styles={{
              control: {
                padding: "1.25rem",
              },
              label: { fontSize: "1rem", fontWeight: 600 },
              content: {
                padding: "0 1.25rem 1.25rem 1.25rem",
                color: computedColorScheme === "dark" ? "#909296" : "#868e96",
                lineHeight: 1.6,
              },
              chevron: { color: "#4a8c71" },
            }}
          >
            {faqs.map((faq, index) => (
              <Accordion.Item key={index} value={`faq-${index}`}>
                <Accordion.Control
                  icon={<IconQuestionMark size={20} color="#4a8c71" />}
                >
                  {faq.question}
                </Accordion.Control>
                <Accordion.Panel>{faq.answer}</Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </AnimateIn>

        <AnimateIn delay={0.2}>
          <Text size="sm" c="dimmed" ta="center" mt={40}>
            Still have questions?{" "}
            <Text
              component="span"
              c="healthyGreen"
              fw={600}
              style={{ cursor: "pointer" }}
              onClick={() => {
                const element = document.getElementById("cta");
                if (element) {
                  element.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }
              }}
            >
              Contact us
            </Text>{" "}
            and we'll be happy to help.
          </Text>
        </AnimateIn>
      </Container>
    </Box>
  );
}
